// #region imports

// --- Angular
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

// --- RxJs
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';

// --- 3rd Party
import { TranslateService } from '@ngx-translate/core';
import { flatten } from 'lodash';

// --- HS Code
import { AppService } from '../app.service';
import {
  Constants,
  GroupJournalEntryModel,
  JournalEntryModel,
  JournalPinJumpToModel,
  JournalQuadrantModel,
  PinnedJournalEntryGroupModel,
  PinnedJournalEntryTextModel,
  StylesTeamRoleTypes,
  ToastEventModel,
  UnpinnedJournalModel
} from '../../../models';
import { AppToastService } from '../app-toast.service';
import { QuadrantsBaseService } from './quadrants-base.service';

// #endregion

@Injectable({
  providedIn: 'root'
})
export abstract class JournalingBaseService {
  private _jumpToLocation: JournalPinJumpToModel | null = null;

  private _openedStyleId$ = new BehaviorSubject<string>('');
  get openedStyleId$(): Observable<string> {
    return this._openedStyleId$;
  }

  protected _allJournalPins$ = new BehaviorSubject<PinnedJournalEntryTextModel[] | null>(null);
  get allJournalPins$(): Observable<PinnedJournalEntryTextModel[] | null> {
    return this._allJournalPins$;
  }

  protected _journalTextEntries$: BehaviorSubject<Map<string, PinnedJournalEntryTextModel[]>>;
  get journalTextEntries$(): Observable<Map<string, PinnedJournalEntryTextModel[]>> {
    return this._journalTextEntries$;
  }

  protected _selectedStyleId$: BehaviorSubject<string | null>;
  get selectedStyleId$(): Observable<string | null> {
    return this._selectedStyleId$;
  }

  get styleJournalTextEntries$(): Observable<PinnedJournalEntryGroupModel | null> {
    return combineLatest([
      this.journalTextEntries$,
      this.selectedStyleId$,
    ]).pipe(
      map(([textEntriesMap, selectedStyleId]) => {
        if (!selectedStyleId || !textEntriesMap.has(selectedStyleId)) {
          return [];
        }
        return textEntriesMap.get(selectedStyleId);
      }), map((textEntries: PinnedJournalEntryTextModel[] | undefined) => {
        if (!textEntries) return null;

        return {
          leader: textEntries.filter(t => t.stylesTeamRoleType === StylesTeamRoleTypes.Leader),
          teamMember: textEntries.filter(t => t.stylesTeamRoleType === StylesTeamRoleTypes.TeamMember),
        }
      })
    );
  }

  protected _entriesByQuadrantId$: BehaviorSubject<JournalEntryModel[] | null>;
  get entriesByQuadrantId$(): Observable<JournalEntryModel[] | null> {
    return this._entriesByQuadrantId$;
  }

  get groupedByStyledId$(): Observable<GroupJournalEntryModel[]> {
    return combineLatest([
      this.quadrantsService.quadrants$,
      this.quadrantsService.currentId$,
      this.entriesByQuadrantId$,
    ])
    .pipe(
      map(([quadrants, quadrantId, entries]): GroupJournalEntryModel[] => {

        if (Array.isArray(quadrants) && Array.isArray(entries)) {
          const styles = flatten(quadrants.map(q => q.categoryModels));
          console.log(styles);
          return styles.filter(style => style.quadrantId === quadrantId).map((style) => {
            return {
              styleId: style.id,
              resourceKey: style.name,
              pinnedJournalEntries: entries.filter(entry => entry.styleId === style.id ).filter( entry => entry.pinLocation),
              unpinnedJournalEntries : entries.filter(entry => entry.styleId === style.id).filter( entry => !entry.pinLocation),
            } as GroupJournalEntryModel;
          });
        }

        return [];
      }),
      tap((s) => console.log(s)),
    );
  }

  protected _journalCreated$: Subject<string>;
  get journalCreated$(): Observable<string> {
    return this._journalCreated$;
  }

  protected _journalPinned$: Subject<string>;
  get journalPinned$(): Observable<string> {
    return this._journalPinned$;
  }

  protected _journalUnpinned$: Subject<string>;
  get journalUnpinned$(): Observable<string> {
    return this._journalUnpinned$;
  }

  protected _journalEntryDeleted$: Subject<string> = new Subject();
  get journalEntryDeleted$(): Observable<string> {
    return this._journalEntryDeleted$;
  }

  protected _journalEntryUpdated$: Subject<boolean> = new Subject();
  get journalEntryUpdated$(): Observable<boolean> {
    return this._journalEntryUpdated$;
  }

  constructor(
    protected appService: AppService,
    protected translateService: TranslateService,
    protected quadrantsService: QuadrantsBaseService,
    protected toastService: AppToastService,
    protected router: Router
  ) {
    this._journalCreated$ = new Subject<string>();
    this._journalPinned$ = new Subject<string>();
    this._journalUnpinned$ = new Subject<string>();
    this._journalTextEntries$ = new BehaviorSubject(new Map());
    this._selectedStyleId$ = new BehaviorSubject<string | null>(null);
    this._entriesByQuadrantId$ = new BehaviorSubject<JournalEntryModel[] | null>(null);

    this.toastService.toastClicked$.subscribe((toastEvent) => {

      if (toastEvent && toastEvent.eventTarget === Constants.JOURNAL_TOAST_CLICK_EVENT_NAME) {
        switch (toastEvent.eventName) {
          case Constants.EVENT_UNDO_DELETE:
            this.undelete();
            break;
          case Constants.EVENT_UNDO_UPDATE:
            this.revert();
            break;
          case Constants.EVENT_NAVIGATE:
            this.router.navigate([toastEvent.eventPayload]);
            break;
        }
      }
    });

    this.quadrantsService.quadrants$.subscribe((quadrants: JournalQuadrantModel[] | null) => {
      if (quadrants === null) {
        return;
      }
      for (const quadrant of quadrants) {
        for (const category of quadrant.categoryModels) {
          this.getPinnedJournalEntryTextByStyleId(category.id.toString());
        }
      }
    });
  }

  abstract getJournalEntriesByQuadrant(quadrantId: string): void;
  abstract getPinnedJournalEntryTextByStyleId(styleId: string): void;
  abstract getJournalEntryById(journalEntryId: string): void;
  abstract createJournalEntry(entry: JournalEntryModel): void;
  abstract pinJournalEntryText(entry: JournalEntryModel): void;
  abstract unpinJournalEntryText(entry: JournalEntryModel): void;
  abstract updateJournalEntryById(journalEntryId: string, entry: JournalEntryModel, canUndo: boolean): void;
  abstract deleteJournalEntry(journalEntry: UnpinnedJournalModel): void;
  abstract undelete(): void;
  abstract revert(): void;
  abstract getAllJournalPins(): void;

  protected _toggleEntryText(entry: JournalEntryModel, pinEntry = true) {
    const textEntries = this._journalTextEntries$.value;
    if (textEntries.has(entry.styleId.toString())) {
      const currentStyle = textEntries.get(entry.styleId.toString());
      const toggledPin = currentStyle?.find(cs => cs.id === entry.pinLocation);
      if (toggledPin) {
        toggledPin.isPinned = pinEntry;
        toggledPin.pinnedJournalEntryId = pinEntry ? entry?.id ?? null : null;
      }

      if (currentStyle) textEntries.set(entry.styleId.toString(), currentStyle);

      this._journalTextEntries$.next(textEntries);
    }
  }

  getResourceKeyByStyleId(styleId: number) {
    switch (styleId) {
      case 1:
        return 'Question_Category_Authentic';
      case 2:
        return 'Question_Category_Transforming';
      case 3:
        return 'Question_Category_Reliable';
      case 4:
        return 'Question_Category_Achieving';
      case 5:
        return 'Question_Category_Encouraging';
      case 6:
        return 'Question_Category_Relating';
      case 7:
        return 'Question_Category_Compassionate';
      case 8:
        return 'Question_Category_Developing';
      case 9:
        return 'Question_Category_Controlling';
      case 10:
        return 'Question_Category_Competitive';
      case 11:
        return 'Question_Category_Striving';
      case 12:
        return 'Question_Category_Sarcastic';
      case 13:
        return 'Question_Category_Approval_Seeking';
      case 14:
        return 'Question_Category_Avoiding';
      case 15:
        return 'Question_Category_Easily_Offended';
      default:
        return 'Question_Category_Dependent';
    }
  }

  setPinJumpToLocation(pinLocation: string | null, quadrantId: string, styleId: number): void {
    this._jumpToLocation = {
      pinLocation,
      quadrantId,
      styleId
    } as JournalPinJumpToModel;
  }

  setOpenedStyleId(styleId: number): void {
    this._openedStyleId$.next(styleId.toString());
  }

  clearOpenedStyleId(): void {
    this._openedStyleId$.next('');
  }

  getPinJumpToLocation() {
    return this._jumpToLocation;
  }

  clearPinJumpToLocation() {
    this._jumpToLocation = null;
  }
}
